import React, {useState, useEffect} from 'react';
import {json, useParams, useNavigate} from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography, Card, CardContent, CircularProgress, Paper, Button, Snackbar, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import ReactDOM from 'react-dom/client'
import parse from 'html-react-parser';

import {
    CreateEbayDescription,
    CreateTitle,
    CreateWooDescription,
    createXmlEbay
} from '../functions/functions';
import { getGem } from '../api/backendApi';

const Preview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [gem, setGem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewContent, setPreviewContent] = useState(null);
    const [htmlContent, setHtmlContent] = useState('');
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    useEffect(() => {
        const fetchGem = async () => {
            try {
                const response = await getGem(id);
                if (response.status === 'ok') {
                    setGem(response.gem);
                } else {
                    setError('Failed to load gem data');
                }
            } catch (err) {
                setError('Error loading gem data');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchGem();
    }, [id]);

    useEffect(() => {
        if (gem) {
            const createHTML = async () => {
                try {
                    const html = await CreateEbayDescription(gem, false);
                    setHtmlContent(html);
                    const parsedContent = parse(html);
                    setPreviewContent(parsedContent);
                } catch (err) {
                    console.error('Error creating HTML:', err);
                    setError('Error generating preview content');
                }
            };
            createHTML();
        }
    }, [gem]);

    const handleCopyHTML = () => {
        navigator.clipboard.writeText(htmlContent)
            .then(() => {
                setShowCopySuccess(true);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    const handleBack = () => {
        navigate(-1); // Go back to the previous page
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 0 }}>
            <Card 
                elevation={3}
                sx={{
                    maxWidth: '100%',
                    mx: 'auto',
                    mb: 0,
                    borderRadius: 0,
                    overflow: 'hidden'
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2,
                        p: 2,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBack}
                                sx={{ mr: 2 }}
                            >
                                Back
                            </Button>
                            <Typography variant="h5">
                                Preview Description
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyHTML}
                            disabled={!htmlContent}
                        >
                            Copy HTML
                        </Button>
                    </Box>
                    <Box sx={{ p: 0 }}>
                        {previewContent}
                    </Box>
                </CardContent>
            </Card>

            <Snackbar
                open={showCopySuccess}
                autoHideDuration={3000}
                onClose={() => setShowCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowCopySuccess(false)} severity="success" sx={{ width: '100%' }}>
                    HTML code copied to clipboard!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Preview;